import {
  Alert,
  Alignment,
  Direction,
  Displaying,
  FontWeight,
  Spacing,
  Typography,
} from "@bonusx-global/bx-design-system";
import type { AlertColor } from "@mui/material/Alert";
import Box from "@mui/material/Box";
import emojiRegex from "emoji-regex";
import type { SurveyStepBodyExtra } from "../../../../legacy/pages/survey/entities/survey-step";
import { resolveSurveyIcon } from "../../utils/resolve-survey-icon";

const ALERT_EXTRA_COMPONENT = "alert";

export const SURVEY_RICH_TEXT_SX = { whiteSpace: "pre-line", "& b, & strong": { fontWeight: FontWeight.bold } };

function toAlertSeverity(variant: string | undefined): AlertColor {
  if (variant === "success" || variant === "warning" || variant === "error") {
    return variant;
  }
  return "info";
}

function renderAlertIcon(icon: string | undefined): JSX.Element | undefined {
  if (!icon) {
    return undefined;
  }
  const resolvedIcon = resolveSurveyIcon(icon);
  if (resolvedIcon) {
    return resolvedIcon;
  }
  const isLiteralEmoji = emojiRegex().test(icon);
  return isLiteralEmoji ? <span>{icon}</span> : undefined;
}

export function isAlertExtra(extra: SurveyStepBodyExtra): boolean {
  return extra.component === ALERT_EXTRA_COMPONENT;
}

interface SurveyExtraAlertsProps {
  extra: SurveyStepBodyExtra[] | undefined;
  fullWidth: boolean;
  maxWidth?: string;
}

function SurveyExtraAlerts({ extra, fullWidth, maxWidth }: Readonly<SurveyExtraAlertsProps>): JSX.Element | null {
  const alerts = extra?.filter(isAlertExtra) ?? [];
  if (alerts.length === 0) {
    return null;
  }

  return (
    <Box
      display={Displaying.flex}
      flexDirection={Direction.column}
      alignItems={fullWidth ? Alignment.stretch : Alignment.center}
      gap={Spacing["1000"]}
      width="100%"
      maxWidth={maxWidth}
    >
      {alerts.map(function renderAlert(alert) {
        return (
          <Alert
            key={`${alert.variant}-${alert.icon}-${alert.description}`}
            severity={toAlertSeverity(alert.variant)}
            icon={renderAlertIcon(alert.icon)}
            sx={{ width: fullWidth ? "100%" : "auto", alignItems: Alignment.center }}
          >
            <Typography
              variant="body2"
              component="div"
              sx={SURVEY_RICH_TEXT_SX}
              dangerouslySetInnerHTML={{ __html: alert.description }}
            />
          </Alert>
        );
      })}
    </Box>
  );
}

export default SurveyExtraAlerts;
